import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Divider,
  FormGroup,
  Checkbox,
  Skeleton,
  Button,
  TextField,
  createFilterOptions,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMemo, useState } from "react";

import { LabeledAutoComplete } from "../../../components/UI/LabeledAutoComplete";
import { useDashboard } from "../../../contexts/DashboardContext";
import { DashboardFiltersDto } from "../../../types/dashboard";

interface DashboardFiltersProps {
  filters: DashboardFiltersDto;
  onFiltersChange: (filters: DashboardFiltersDto) => void;
}

export const DashboardFilters = ({
  filters,
  onFiltersChange,
}: DashboardFiltersProps) => {
  const {
    currentFilters,
    toggleCellFilter,
    toggleClientFilter,
    eventControlOptions,
  } = useDashboard();
  const [lastAction, setLastAction] = useState<"clear" | "apply">("clear");

  // Usar as opções do endpoint event-control/options
  const statusOptions = eventControlOptions?.statuses ?? [];

  const cellOptions = useMemo(() => {
    return (eventControlOptions?.cells ?? [])
      .map((c) => ({ id: c.id, name: c.name.trim().replace(/\s+/g, " ") }))
      .filter((c) => c.id && c.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eventControlOptions]);

  const clientOptions = useMemo(() => {
    return (eventControlOptions?.clients ?? [])
      .map((c) => ({ id: c.id, name: c.name.trim().replace(/\s+/g, " ") }))
      .filter((c) => c.id && c.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eventControlOptions]);

  const autocompleteFilter = useMemo(
    () =>
      createFilterOptions<{ id: string; name: string }>({
        stringify: (option) => option?.name || "",
        ignoreCase: true,
        trim: true,
      }),
    [],
  );

  const selectedCell =
    cellOptions.find((c) => c.id === currentFilters.cellId) || null;
  const selectedClient =
    clientOptions.find((c) => c.id === currentFilters.clientId) || null;

  const [cellInput, setCellInput] = useState("");
  const [clientInput, setClientInput] = useState("");

  // Verificar se há filtros aplicados
  const hasFilters =
    Object.keys(filters).length > 0 &&
    (filters.status?.length ||
      filters.startDate?.start ||
      filters.startDate?.end ||
      filters.endDate?.start ||
      filters.endDate?.end ||
      filters.eventScope ||
      filters.hasCompetitor !== undefined);

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];

    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
    setLastAction("clear");
  };

  const handleApplyFilters = () => {
    if (!hasFilters) {
      handleClearFilters();
      return;
    }

    const hasValidFilters =
      (filters.status && filters.status.length > 0) ||
      filters.startDate?.start ||
      filters.startDate?.end ||
      filters.endDate?.start ||
      filters.endDate?.end ||
      filters.eventScope ||
      filters.hasCompetitor !== undefined;

    if (hasValidFilters) {
      onFiltersChange(filters);
      setLastAction("apply");
    }
  };

  return (
    <Box sx={{ width: "320px" }}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
            Filtros
          </Typography>
          {hasFilters && (
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                fontSize: 14,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Limpar Filtros
            </Button>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Início do evento
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              label="De"
              value={filters.startDate?.start || null}
              onChange={(date: Date | null) =>
                onFiltersChange({
                  ...filters,
                  startDate: {
                    ...filters.startDate,
                    start: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />

            <DatePicker
              label="Até"
              value={filters.startDate?.end || null}
              onChange={(date: Date | null) =>
                onFiltersChange({
                  ...filters,
                  startDate: {
                    ...filters.startDate,
                    end: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Finalização do evento
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              label="De"
              value={filters.endDate?.start || null}
              onChange={(date: Date | null) =>
                onFiltersChange({
                  ...filters,
                  endDate: {
                    ...filters.endDate,
                    start: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />

            <DatePicker
              label="Até"
              value={filters.endDate?.end || null}
              onChange={(date: Date | null) =>
                onFiltersChange({
                  ...filters,
                  endDate: {
                    ...filters.endDate,
                    end: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <LabeledAutoComplete
            label="Cliente"
            options={clientOptions}
            value={selectedClient}
            inputValue={clientInput}
            filterOptions={autocompleteFilter}
            onInputChange={(_, value, reason) => {
              if (value === "" && reason === "reset") return;
              setClientInput(value);
            }}
            onChange={(_, newValue: any) => {
              const nextId = newValue?.id || "";
              const currentId = currentFilters.clientId || "";
              if (nextId === currentId) return;
              if (currentId) toggleClientFilter(currentId);
              if (nextId) toggleClientFilter(nextId);
            }}
            getOptionLabel={(option: any) => option?.name || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.id === value?.id
            }
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione um cliente"
                style={{ background: "#FFF" }}
              />
            )}
          />

          <LabeledAutoComplete
            label="Célula"
            options={cellOptions}
            value={selectedCell}
            inputValue={cellInput}
            filterOptions={autocompleteFilter}
            onInputChange={(_, value, reason) => {
              if (value === "" && reason === "reset") return;
              setCellInput(value);
            }}
            onChange={(_, newValue: any) => {
              const nextId = newValue?.id || "";
              const currentId = currentFilters.cellId || "";
              if (nextId === currentId) return;
              if (currentId) toggleCellFilter(currentId);
              if (nextId) toggleCellFilter(nextId);
            }}
            getOptionLabel={(option: any) => option?.name || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.id === value?.id
            }
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione uma célula"
                style={{ background: "#FFF" }}
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Evento */}
        <FormControl fullWidth size="small">
          <InputLabel>Evento</InputLabel>
          <Select
            value={filters.eventScope || "Todos"}
            label="Evento"
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                eventScope:
                  e.target.value === "Todos" ? undefined : e.target.value,
              })
            }
          >
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Nacional">Nacional</MenuItem>
            <MenuItem value="Internacional">Internacional</MenuItem>
            <MenuItem value="Ambos">Ambos</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Concorrência</InputLabel>
            <Select
              value={
                filters.hasCompetitor === undefined
                  ? "Todos"
                  : filters.hasCompetitor
                  ? "Sim"
                  : "Não"
              }
              label="Concorrência"
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  hasCompetitor:
                    e.target.value === "Todos"
                      ? undefined
                      : e.target.value === "Sim",
                })
              }
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="Sim">Sim</MenuItem>
              <MenuItem value="Não">Não</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Status MM */}
        <Box display={"flex"} flexDirection={"column"}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Status MM
          </Typography>

          <FormGroup>
            {statusOptions.length === 0
              ? [...Array(6)].map((_, index) => (
                  <Box
                    key={index}
                    display={"flex"}
                    justifyContent={"space-between"}
                    width={"100%"}
                    mb={1}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="rectangular" width={20} height={20} />
                      <Skeleton variant="text" width={120} />
                    </Box>
                    <Skeleton variant="text" width={30} />
                  </Box>
                ))
              : statusOptions.map((option) => (
                  <Box
                    key={option.id}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      sx={{ fontSize: 14 }}
                      control={
                        <Checkbox
                          checked={filters.status?.includes(option.id) || false}
                          onChange={() => handleStatusChange(option.id)}
                          size="small"
                          sx={{ fontSize: 14 }}
                        />
                      }
                      label={
                        <Typography variant="body2">{option.name}</Typography>
                      }
                    />
                    <Typography variant="body2">{option.count}</Typography>
                  </Box>
                ))}
          </FormGroup>
        </Box>

        <Box mt={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            disabled={!hasFilters && lastAction === "clear"}
            sx={{
              textTransform: "none",
              bgcolor: "#142A3D",
              "&:hover": {
                bgcolor: "#1E3C56",
              },
            }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
